// filepath: apps/web/src/features/proc/ExecutionTraceViewer.tsx
//
// FR-UI-PROC-1 AC-1 — execution-trace viewer body.
//
// Renders one row per step-log entry in `started_at ASC` order.
// Surfaces the canonical fields the requirement-validation AC-1
// enumerates:
//   - step_node_id
//   - status
//   - retry_count
//   - started_at, completed_at (when set)
//   - duration_ms (when completed)
//   - error_message (when failed)
//
// FR-UI-NFR-2 — the SPA NEVER parses `error_message` as code or
// evaluates it. It is rendered as a text node. The trace viewer
// treats step-log entries as data only.
//
// FR-UI-CORE-10 — the 5-state error contract is applied to the
// fetch (loading / fetch-failure / permission-denied / stale-version
// / rate-limit).

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { SkeletonLayout } from "@/design-system/error-states/SkeletonLayout";
import { FetchError } from "@/design-system/error-states/FetchError";
import { PermissionDenied } from "@/design-system/error-states/PermissionDenied";
import { StaleVersionError } from "@/design-system/error-states/StaleVersionError";
import { RateLimitBackpressure } from "@/design-system/error-states/RateLimitBackpressure";
import { useStepLog } from "./useStepLog";
import { classifyStepLogError } from "./index-shared";
import type { StepLogEntry } from "./types";

export interface ExecutionTraceViewerProps {
  /** Process instance UUID. */
  instanceId: string;
}

/**
 * ExecutionTraceViewer — page body for the per-instance trace view.
 *
 * The classification helper lives in `./index-shared` and is
 * re-exported through the public API. This file consumes the
 * function via direct import to avoid a barrel-import cycle.
 */

export function ExecutionTraceViewer({
  instanceId,
}: ExecutionTraceViewerProps): React.ReactElement {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useStepLog({ instanceId });

  if (isLoading) {
    return (
      <section
        data-testid="execution-trace-viewer--loading"
        aria-labelledby="execution-trace-viewer-title"
        className="execution-trace-viewer execution-trace-viewer--loading"
      >
        <h2 id="execution-trace-viewer-title" className="visually-hidden">
          {t("trace.title", "Execution trace")}
        </h2>
        <SkeletonLayout />
      </section>
    );
  }

  if (error !== null) {
    const status = classifyStepLogError(error);
    if (status === "permission-denied") {
      return (
        <section
          data-testid="execution-trace-viewer--permission-denied"
          aria-labelledby="execution-trace-viewer-title"
          className="execution-trace-viewer execution-trace-viewer--permission-denied"
        >
          <h2 id="execution-trace-viewer-title" className="visually-hidden">
            {t("trace.title", "Execution trace")}
          </h2>
          <PermissionDenied />
        </section>
      );
    }
    if (status === "stale-version") {
      return (
        <section
          data-testid="execution-trace-viewer--stale-version"
          aria-labelledby="execution-trace-viewer-title"
          className="execution-trace-viewer execution-trace-viewer--stale-version"
        >
          <h2 id="execution-trace-viewer-title" className="visually-hidden">
            {t("trace.title", "Execution trace")}
          </h2>
          <StaleVersionError />
        </section>
      );
    }
    if (status === "rate-limit") {
      return (
        <section
          data-testid="execution-trace-viewer--rate-limit"
          aria-labelledby="execution-trace-viewer-title"
          className="execution-trace-viewer execution-trace-viewer--rate-limit"
        >
          <h2 id="execution-trace-viewer-title" className="visually-hidden">
            {t("trace.title", "Execution trace")}
          </h2>
          <RateLimitBackpressure retryAfterSeconds={error.retryAfterSeconds} />
        </section>
      );
    }
    return (
      <section
        data-testid="execution-trace-viewer--fetch-failure"
        aria-labelledby="execution-trace-viewer-title"
        className="execution-trace-viewer execution-trace-viewer--fetch-failure"
      >
        <h2 id="execution-trace-viewer-title" className="visually-hidden">
          {t("trace.title", "Execution trace")}
        </h2>
        <FetchError
          message={t(
            "trace.state.fetch_failure",
            "Failed to load the step log.",
          )}
          onRetry={() => {
            void refetch();
          }}
        />
      </section>
    );
  }

  const entries: StepLogEntry[] = data ?? [];

  if (entries.length === 0) {
    return (
      <section
        data-testid="execution-trace-viewer--empty"
        aria-labelledby="execution-trace-viewer-title"
        className="execution-trace-viewer execution-trace-viewer--empty"
      >
        <h2 id="execution-trace-viewer-title" className="visually-hidden">
          {t("trace.title", "Execution trace")}
        </h2>
        <p
          role="status"
          aria-live="polite"
          data-testid="execution-trace-viewer-empty"
        >
          {t("trace.empty", "No step log entries.")}
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="execution-trace-viewer"
      aria-labelledby="execution-trace-viewer-title"
      className="execution-trace-viewer"
    >
      <h2 id="execution-trace-viewer-title" className="visually-hidden">
        {t("trace.title", "Execution trace")}
      </h2>
      <table
        data-testid="execution-trace-table"
        className="execution-trace-table"
        aria-label={t("trace.table_aria_label", "Execution trace step log")}
      >
        <thead>
          <tr>
            <th scope="col">{t("trace.column.node_id", "Node")}</th>
            <th scope="col">{t("trace.column.status", "Status")}</th>
            <th scope="col">{t("trace.column.retry_count", "Retries")}</th>
            <th scope="col">{t("trace.column.started_at", "Started")}</th>
            <th scope="col">{t("trace.column.duration_ms", "Duration")}</th>
            <th scope="col">{t("trace.column.error_message", "Error")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, idx) => (
            <tr
              key={`${entry.step_node_id}-${idx}-${entry.started_at}`}
              data-testid="execution-trace-row"
              data-node-id={entry.step_node_id}
              data-status={entry.status}
            >
              <td className="execution-trace-cell-node">
                {entry.step_node_id}
              </td>
              <td className="execution-trace-cell-status">
                <span
                  data-testid="execution-trace-row-status"
                  data-state={entry.status}
                  className="execution-trace-status"
                >
                  {entry.status}
                </span>
              </td>
              <td className="execution-trace-cell-retries">
                {entry.retry_count}
              </td>
              <td className="execution-trace-cell-started">
                {entry.started_at}
              </td>
              <td className="execution-trace-cell-duration">
                {entry.duration_ms !== undefined
                  ? `${entry.duration_ms}ms`
                  : ""}
              </td>
              <td className="execution-trace-cell-error">
                {entry.error_message ?? ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
